// @intent: Versioned postMessage protocol between EditorV2 parent and playable iframe runtime
// @flow: apps/web sends ParentToRuntimeMessage; apps/playable-runtime validates and replies with RuntimeToParentMessage
// @stale: when R6/R7/R8 add runtime commands, bump schemas without accepting unversioned messages

import { PatchSchema, ProjectSchema } from '@mintyads/playable-api';
import type { Patch, Project } from '@mintyads/playable-api';
import { z } from 'zod';

export const PLAYABLE_RUNTIME_PROTOCOL_VERSION = 'playable_runtime.v1' as const;

const JsonPrimitiveSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export type JsonValue =
  | z.infer<typeof JsonPrimitiveSchema>
  | { readonly [key: string]: JsonValue }
  | readonly JsonValue[];

export const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([JsonPrimitiveSchema, z.array(JsonValueSchema), z.record(z.string(), JsonValueSchema)]),
);

const FiniteNumberSchema = z.number().refine(Number.isFinite, 'Expected finite number');

export const ViewportSchema = z.strictObject({
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});

export const RuntimeModeSchema = z.enum(['edit', 'play']);
export const RuntimeInputGestureSchema = z.enum(['pointer', 'tap', 'swipe', 'drag']);
export const RuntimeInputPhaseSchema = z.enum(['raw', 'accepted']);
export const RuntimeInputDirectionSchema = z.enum(['left', 'right', 'up', 'down']);
export const RuntimeInputPayloadSchema = z.strictObject({
  gesture: RuntimeInputGestureSchema,
  phase: RuntimeInputPhaseSchema,
  designX: z.number(),
  designY: z.number(),
  pointerId: z.number().int(),
  pointerType: z.string().min(1),
  isPrimary: z.boolean(),
  direction: RuntimeInputDirectionSchema.optional(),
});

// A client-pixel rectangle (board / CTA bounding box) reported in a match3 snapshot for the §10 spatial CTA-occlusion check.
export const RuntimeRectSchema = z.strictObject({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
});
export type RuntimeRect = z.infer<typeof RuntimeRectSchema>;

export const RuntimeDesignPointSchema = z.strictObject({
  x: z.number(),
  y: z.number(),
});
export const RuntimeDesignMoveSchema = z.strictObject({
  from: RuntimeDesignPointSchema,
  to: RuntimeDesignPointSchema,
});

// Parent → runtime: drive a KNOWN move (the §10 verifier drives config.board.firstMove / findValidSwap, never arbitrary
// input). Coordinates are in DESIGN space; the runtime converts to client px and synthesizes real canvas pointer events.
export const RuntimeDriveInputPayloadSchema = z.strictObject({
  gesture: z.enum(['drag', 'tap']),
  fromDesignX: z.number(),
  fromDesignY: z.number(),
  toDesignX: z.number(),
  toDesignY: z.number(),
});
export type RuntimeDriveInputPayload = z.infer<typeof RuntimeDriveInputPayloadSchema>;

export const RuntimeProbeCoordinateSpaceSchema = z.enum(['design', 'client']);
export const RuntimeProbePointSchema = z.strictObject({
  coordinateSpace: RuntimeProbeCoordinateSpaceSchema,
  x: FiniteNumberSchema,
  y: FiniteNumberSchema,
});
export const RuntimeProbeActionPayloadSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('tap'),
    at: RuntimeProbePointSchema,
  }),
  z.strictObject({
    kind: z.literal('swipe'),
    from: RuntimeProbePointSchema,
    to: RuntimeProbePointSchema,
    durationMs: z.number().int().positive().optional(),
  }),
  z.strictObject({
    kind: z.literal('drag'),
    from: RuntimeProbePointSchema,
    to: RuntimeProbePointSchema,
    durationMs: z.number().int().positive().optional(),
  }),
  z.strictObject({
    kind: z.literal('resize'),
    viewport: ViewportSchema,
  }),
  z.strictObject({
    kind: z.literal('wait'),
    durationMs: z.number().int().positive(),
  }),
  z.strictObject({
    kind: z.literal('query'),
    label: z.string().min(1),
  }),
]);
export type RuntimeProbeActionPayload = z.infer<typeof RuntimeProbeActionPayloadSchema>;

export const RuntimeProbeRectSchema = z.strictObject({
  x: FiniteNumberSchema,
  y: FiniteNumberSchema,
  width: FiniteNumberSchema,
  height: FiniteNumberSchema,
});

export const RuntimeProbeTransformSchema = z.strictObject({
  designViewport: ViewportSchema,
  runtimeViewport: ViewportSchema,
  canvasClientRect: RuntimeProbeRectSchema,
  scaleX: FiniteNumberSchema,
  scaleY: FiniteNumberSchema,
  offsetX: FiniteNumberSchema,
  offsetY: FiniteNumberSchema,
  devicePixelRatio: FiniteNumberSchema,
});

// @intent AI-readable per-layer pointer metadata captured by runtime probes.
// @flow PixiRuntimeCanvas collector -> runtime_probe.v1 snapshot -> verifier/CLI reports.
// @stale Keep synchronized with Pixi v8 eventMode semantics and runtimeProbeCollector.ts.
export const RuntimeProbeLayerInteractivitySchema = z.strictObject({
  eventMode: z.string().min(1).optional(),
  interactive: z.boolean().optional(),
  hitArea: RuntimeProbeRectSchema.nullable(),
  acceptsPointerEvents: z.boolean(),
});

// @intent AI-readable layer role/classification for genre verifiers beyond screenshots.
// @flow Explicit/generated metadata or conservative id inference -> runtime_probe.v1 snapshot.
// @stale Keep role strings open-ended; genre-specific verifiers should check flags, not a closed enum.
export const RuntimeProbeLayerSemanticSchema = z.strictObject({
  role: z.string().min(1),
  source: z.enum(['explicit', 'id', 'type', 'unknown']),
  flags: z.array(z.string().min(1)).default([]),
});

// @intent AI-readable layer content that screenshots hide from symbolic agents.
// @flow Pixi target text/direct assetUrl -> runtime_probe.v1 layer snapshot -> action-diff/verifier reports.
// @stale Keep optional and additive; old traces must remain valid.
export const RuntimeProbeLayerContentSchema = z.strictObject({
  text: z.string().optional(),
  assetUrl: z.string().optional(),
});

export const RuntimeProbeLayerSnapshotSchema = z.strictObject({
  id: z.string().min(1),
  type: z.string().min(1),
  parentId: z.string().min(1).nullable().optional(),
  depth: z.number().int().nonnegative().optional(),
  visible: z.boolean(),
  renderable: z.boolean().optional(),
  alpha: FiniteNumberSchema.optional(),
  local: RuntimeProbeRectSchema.nullable(),
  world: RuntimeProbeRectSchema.nullable(),
  client: RuntimeProbeRectSchema.nullable(),
  x: FiniteNumberSchema.optional(),
  y: FiniteNumberSchema.optional(),
  scaleX: FiniteNumberSchema.optional(),
  scaleY: FiniteNumberSchema.optional(),
  rotation: FiniteNumberSchema.optional(),
  zIndex: FiniteNumberSchema.optional(),
  texture: z.strictObject({
    assetUrl: z.string().optional(),
    naturalWidth: FiniteNumberSchema.optional(),
    naturalHeight: FiniteNumberSchema.optional(),
    displayWidth: FiniteNumberSchema.optional(),
    displayHeight: FiniteNumberSchema.optional(),
    fillRatio: FiniteNumberSchema.optional(),
  }).optional(),
  content: RuntimeProbeLayerContentSchema.optional(),
  interactivity: RuntimeProbeLayerInteractivitySchema.optional(),
  semantic: RuntimeProbeLayerSemanticSchema.optional(),
  tags: z.array(z.string()).default([]),
});

export const RuntimeProbeTweenSnapshotSchema = z.strictObject({
  id: z.string().min(1),
  targetLayerId: z.string().nullable(),
  props: z.record(z.string(), z.strictObject({
    from: FiniteNumberSchema,
    to: FiniteNumberSchema,
    current: FiniteNumberSchema,
  })),
  easing: z.string().min(1),
  startedAtMs: FiniteNumberSchema,
  durationMs: FiniteNumberSchema,
  progress: FiniteNumberSchema.pipe(z.number().min(0).max(1)),
  status: z.enum(['running', 'finished', 'stopped']),
});

export const RuntimeProbeInputEventSchema = z.strictObject({
  type: z.enum(['pointerdown', 'pointermove', 'pointerup', 'pointercancel', 'tap', 'swipe', 'drag']),
  phase: z.enum(['raw', 'mapped', 'accepted', 'rejected']),
  clientX: FiniteNumberSchema,
  clientY: FiniteNumberSchema,
  designX: FiniteNumberSchema,
  designY: FiniteNumberSchema,
  pointerId: z.number().int(),
  pointerType: z.string().min(1),
  hitLayerIds: z.array(z.string()),
  acceptedBy: z.array(z.string()),
  direction: RuntimeInputDirectionSchema.optional(),
});

export const RuntimeProbeSnapshotV1Schema = z.strictObject({
  schemaVersion: z.literal('runtime_probe.v1'),
  projectId: z.string(),
  runtimeVersion: z.string().min(1),
  capturedAtMs: FiniteNumberSchema,
  frameIndex: z.number().int().nonnegative(),
  label: z.string(),
  transform: RuntimeProbeTransformSchema,
  layers: z.array(RuntimeProbeLayerSnapshotSchema),
  activeTweens: z.array(RuntimeProbeTweenSnapshotSchema),
  recentInput: z.array(RuntimeProbeInputEventSchema),
  stateProviders: z.record(z.string(), JsonValueSchema),
  warnings: z.array(z.string()),
});

export const RuntimeProbeActionRecordSchema = z.strictObject({
  id: z.string().min(1),
  kind: z.enum(['tap', 'swipe', 'drag', 'resize', 'wait', 'query']),
  startedAtMs: FiniteNumberSchema,
  finishedAtMs: FiniteNumberSchema,
  input: z.record(z.string(), JsonValueSchema),
  result: z.enum(['accepted', 'rejected', 'timeout', 'error']),
  summary: z.string(),
});

export const RuntimeProbeActionResultSchema = RuntimeProbeActionRecordSchema.extend({
  before: RuntimeProbeSnapshotV1Schema.optional(),
  after: RuntimeProbeSnapshotV1Schema.optional(),
});

export const RuntimeProbeTraceV1Schema = z.strictObject({
  schemaVersion: z.literal('runtime_probe_trace.v1'),
  projectId: z.string(),
  scenario: z.string(),
  startedAtMs: FiniteNumberSchema,
  finishedAtMs: FiniteNumberSchema,
  snapshots: z.array(RuntimeProbeSnapshotV1Schema),
  actions: z.array(RuntimeProbeActionRecordSchema),
  summary: z.strictObject({
    layerCount: z.number().int().nonnegative(),
    inputEvents: z.number().int().nonnegative(),
    tweenCount: z.number().int().nonnegative(),
    warnings: z.array(z.string()),
  }),
});

export type RuntimeProbeSnapshotV1 = z.infer<typeof RuntimeProbeSnapshotV1Schema>;
export type RuntimeProbeTraceV1 = z.infer<typeof RuntimeProbeTraceV1Schema>;

const RuntimeProjectSchema = ProjectSchema as z.ZodType<Project>;
const RuntimePatchSchema = PatchSchema as z.ZodType<Patch>;
export type RuntimePatch = Patch;
export type RuntimePatchOp = Patch[number];

function envelope<TType extends string, TPayload extends z.ZodType>(
  type: TType,
  payload: TPayload,
) {
  return z.strictObject({
    version: z.literal(PLAYABLE_RUNTIME_PROTOCOL_VERSION),
    type: z.literal(type),
    requestId: z.string().optional(),
    payload,
  });
}

export const ParentToRuntimeMessageSchema = z.discriminatedUnion('type', [
  envelope('runtime.init', z.strictObject({
    project: RuntimeProjectSchema,
    mode: RuntimeModeSchema,
    viewport: ViewportSchema,
    highlightedLayerIds: z.array(z.string()),
  })),
  envelope('runtime.setProject', z.strictObject({ project: RuntimeProjectSchema })),
  envelope('runtime.applyPatch', z.strictObject({
    patch: RuntimePatchSchema,
    project: RuntimeProjectSchema,
  })),
  envelope('runtime.setMode', z.strictObject({ mode: RuntimeModeSchema })),
  envelope('runtime.highlightLayers', z.strictObject({
    ids: z.array(z.string()),
    durationMs: z.number().int().positive(),
  })),
  envelope('runtime.clearHighlights', z.strictObject({})),
  envelope('runtime.restart', z.strictObject({ project: RuntimeProjectSchema })),
  envelope('runtime.ping', z.strictObject({ sentAt: z.number().int().nonnegative() })),
  envelope('runtime.queryState', z.strictObject({})),
  // §10 verifier: drive a known valid move + request a match3 board/CTA snapshot.
  envelope('runtime.driveInput', RuntimeDriveInputPayloadSchema),
  envelope('runtime.queryMatch3', z.strictObject({})),
  envelope('runtime.probeSnapshot', z.strictObject({
    label: z.string().min(1).optional(),
  })),
  envelope('runtime.probeStartTrace', z.strictObject({
    scenario: z.string().min(1),
    sampleEveryMs: z.number().int().positive().optional(),
  })),
  envelope('runtime.probeStopTrace', z.strictObject({})),
  envelope('runtime.probeDriveAction', RuntimeProbeActionPayloadSchema),
]);

export const RuntimeToParentMessageSchema = z.discriminatedUnion('type', [
  envelope('runtime.ready', z.strictObject({ runtimeVersion: z.string().min(1) })),
  envelope('runtime.projectRendered', z.strictObject({
    projectId: z.string(),
    layerCount: z.number().int().nonnegative(),
    assetCount: z.number().int().nonnegative(),
  })),
  envelope('runtime.patchApplied', z.strictObject({
    requestId: z.string(),
    ok: z.boolean(),
    patchPaths: z.array(z.string()),
  })),
  envelope('runtime.layerSelected', z.strictObject({ layerId: z.string().nullable() })),
  envelope('runtime.analytics', z.strictObject({
    name: z.string().min(1),
    data: z.record(z.string(), JsonValueSchema),
  })),
  envelope('runtime.input', RuntimeInputPayloadSchema),
  envelope('runtime.error', z.strictObject({
    requestId: z.string().optional(),
    code: z.string().min(1),
    message: z.string().min(1),
  })),
  envelope('runtime.pong', z.strictObject({
    sentAt: z.number().int().nonnegative(),
    receivedAt: z.number().int().nonnegative(),
  })),
  envelope('runtime.stateSnapshot', z.strictObject({
    projectId: z.string(),
    runtimeVersion: z.string().min(1),
    layerIds: z.array(z.string()),
    mountedLayerIds: z.array(z.string()),
    assetUrls: z.array(z.string()),
    viewport: ViewportSchema,
    errorCount: z.number().int().nonnegative(),
  })),
  // §10 verifier reply: client-px board + CTA + tutorial-hand bounds + logical board/objective state. The driver derives
  // tutorialHandAtFirstMove (hand bounds overlap the firstMove cell) + the board-cropped frame hash from boardBounds.
  envelope('runtime.match3Snapshot', z.strictObject({
    boardBounds: RuntimeRectSchema.nullable(),
    ctaBounds: RuntimeRectSchema.nullable(),
    tutorialHandBounds: RuntimeRectSchema.nullable(),
    firstMoveDesign: RuntimeDesignMoveSchema.nullable(),
    nextValidSwapDesign: RuntimeDesignMoveSchema.nullable(),
    boardTileCount: z.number().int().nonnegative(),
    objectiveRemaining: z.number().int(),
    ctaShown: z.boolean(),
  })),
  envelope('runtime.probeSnapshotResult', RuntimeProbeSnapshotV1Schema),
  envelope('runtime.probeActionResult', RuntimeProbeActionResultSchema),
  envelope('runtime.probeTraceResult', RuntimeProbeTraceV1Schema),
]);

export type ParentToRuntimeMessage = z.infer<typeof ParentToRuntimeMessageSchema>;
export type RuntimeToParentMessage = z.infer<typeof RuntimeToParentMessageSchema>;
export type RuntimeProtocolMessage = ParentToRuntimeMessage | RuntimeToParentMessage;
export type ParentToRuntimeMessageType = ParentToRuntimeMessage['type'];
export type RuntimeToParentMessageType = RuntimeToParentMessage['type'];
export type RuntimeMode = z.infer<typeof RuntimeModeSchema>;
export type RuntimeInputPayload = z.infer<typeof RuntimeInputPayloadSchema>;

type RuntimeProtocolMessageFor<TType extends RuntimeProtocolMessage['type']> = Extract<
  RuntimeProtocolMessage,
  { readonly type: TType }
>;

export function createRuntimeMessage<TType extends RuntimeProtocolMessage['type']>(
  type: TType,
  payload: RuntimeProtocolMessageFor<TType>['payload'],
  requestId?: string,
): RuntimeProtocolMessageFor<TType> {
  return {
    version: PLAYABLE_RUNTIME_PROTOCOL_VERSION,
    type,
    payload,
    ...(requestId === undefined ? {} : { requestId }),
  } as RuntimeProtocolMessageFor<TType>;
}

export function isRuntimeMessage<T extends ParentToRuntimeMessage | RuntimeToParentMessage>(
  value: unknown,
  schema: z.ZodType<T>,
): value is T {
  return schema.safeParse(value).success;
}
